import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useFleetSettings, useSaveFleetSettings, FleetSettingsData } from "@/hooks/use-fleet-settings";
import {
  Settings as SettingsIcon, 
  Bell, 
  Database, 
  Users, 
  Shield, 
  Palette,
  Save,
  RefreshCw,
  Building2,
  Plus,
  Edit,
  Trash2,
  Tag,
  Radio,
  FileText
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useCostCenters } from "@/hooks/use-cost-centers";
import { useDeleteCostCenter } from "@/hooks/use-cost-centers-management";
import { CostCenterModal } from "@/components/fleet/CostCenterModal";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { useFleetProviderTypes, useDeleteFleetProviderType } from "@/hooks/use-fleet-provider-types";
import { ProviderTypeModal } from "@/components/fleet/ProviderTypeModal";
import { useHasGpsTracking } from "@/hooks/useHasGpsTracking";
import { GpsSettingsContent } from "@/components/fleet/settings/GpsSettingsContent";

interface FleetSettings {
  company_id: string;
  notifications: {
    maintenance_alerts: boolean;
    fuel_alerts: boolean;
    document_expiry: boolean;
    violation_alerts: boolean;
    email_notifications: boolean;
    sms_notifications: boolean;
  };
  general: {
    timezone: string;
    date_format: string;
    currency: string;
    fuel_unit: string;
    distance_unit: string;
    working_hours_start: string;
    working_hours_end: string;
  };
  telemetry: {
    provider: string;
    api_key?: string;
    sync_interval: number;
    real_time_tracking: boolean;
  };
  permissions: {
    all_users_view_costs: boolean;
    restrict_vehicle_assignment: boolean;
    require_approval_maintenance: boolean;
  };
}

export default function FleetSettings() {
  const { profile } = useAuth();
  const [costCenterModalOpen, setCostCenterModalOpen] = useState(false);
  const [selectedCostCenter, setSelectedCostCenter] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [costCenterToDelete, setCostCenterToDelete] = useState(null);
  const [providerTypeModalOpen, setProviderTypeModalOpen] = useState(false);
  const [selectedProviderType, setSelectedProviderType] = useState(null);
  const [deleteProviderTypeDialogOpen, setDeleteProviderTypeDialogOpen] = useState(false);
  const [providerTypeToDelete, setProviderTypeToDelete] = useState(null);

  // Get current user's company
  const { data: userCompanies } = useQuery({
    queryKey: ["userCompanies"],
    queryFn: async () => {
      const { data } = await supabase
        .from("user_companies")
        .select("company_id, companies(name)")
        .eq("user_id", profile?.id);
      return data || [];
    },
    enabled: !!profile?.id
  });

  const currentCompanyId = userCompanies?.[0]?.company_id;
  
  // Check if company has GPS tracking
  const { hasGps } = useHasGpsTracking(currentCompanyId);

  // Fetch and save settings using hooks
  const { data: settingsData } = useFleetSettings(currentCompanyId);
  const saveSettingsMutation = useSaveFleetSettings(currentCompanyId || "");
  
  // Cost centers management
  const { data: costCenters } = useCostCenters();
  const deleteMutation = useDeleteCostCenter();
  
  // Provider types management
  const { data: providerTypes } = useFleetProviderTypes(currentCompanyId);
  const deleteProviderTypeMutation = useDeleteFleetProviderType(currentCompanyId || "");
  
  const [settings, setSettings] = useState<FleetSettingsData | null>(null);

  useEffect(() => {
    if (settingsData) {
      setSettings(settingsData);
    }
  }, [settingsData]);

  const updateNotificationSetting = (key: keyof FleetSettings["notifications"], value: boolean) => {
    setSettings(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [key]: value
      }
    }));
  };

  const updateGeneralSetting = (key: keyof FleetSettings["general"], value: string) => {
    setSettings(prev => ({
      ...prev,
      general: {
        ...prev.general,
        [key]: value
      }
    }));
  };

  const updateTelemetrySetting = (key: keyof FleetSettings["telemetry"], value: any) => {
    setSettings(prev => ({
      ...prev,
      telemetry: {
        ...prev.telemetry,
        [key]: value
      }
    }));
  };

  const updatePermissionSetting = (key: keyof FleetSettings["permissions"], value: boolean) => {
    setSettings(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [key]: value
      }
    }));
  };

  const handleSaveSettings = () => {
    if (settings) {
      saveSettingsMutation.mutate(settings);
    }
  };

  const handleEditCostCenter = (costCenter: any) => {
    setSelectedCostCenter(costCenter);
    setCostCenterModalOpen(true);
  };

  const handleDeleteCostCenter = (costCenter: any) => {
    setCostCenterToDelete(costCenter);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteCostCenter = async () => {
    if (costCenterToDelete) {
      await deleteMutation.mutateAsync(costCenterToDelete.id);
      setDeleteDialogOpen(false);
      setCostCenterToDelete(null);
    }
  };

  const activeCostCenters = costCenters?.filter(cc => cc.status === 'active') || [];
  const inactiveCostCenters = costCenters?.filter(cc => cc.status === 'inactive') || [];

  const handleEditProviderType = (type: any) => {
    setSelectedProviderType(type);
    setProviderTypeModalOpen(true);
  };

  const handleDeleteProviderType = (type: any) => {
    setProviderTypeToDelete(type);
    setDeleteProviderTypeDialogOpen(true);
  };

  const confirmDeleteProviderType = async () => {
    if (providerTypeToDelete) {
      await deleteProviderTypeMutation.mutateAsync(providerTypeToDelete.id);
      setDeleteProviderTypeDialogOpen(false);
      setProviderTypeToDelete(null);
    }
  };

  if (!settings) {
    return <div>Carregando configurações...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Configurações</h1>
          <p className="text-muted-foreground">
            Configure as preferências do módulo FleetONE
          </p>
        </div>
        <Button onClick={handleSaveSettings} disabled={saveSettingsMutation.isPending}>
          {saveSettingsMutation.isPending ? (
            <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          Salvar Alterações
        </Button>
      </div>

      <Tabs defaultValue="general" className="space-y-4">
        <TabsList className="flex-wrap">
          <TabsTrigger value="general">Geral</TabsTrigger>
          <TabsTrigger value="notifications">Notificações</TabsTrigger>
          <TabsTrigger value="telemetry">Telemetria</TabsTrigger>
          <TabsTrigger value="permissions">Permissões</TabsTrigger>
          <TabsTrigger value="cost-centers">Centro de Custo</TabsTrigger>
          <TabsTrigger value="provider-types">Tipos de Fornecedor</TabsTrigger>
          <TabsTrigger value="reports" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Relatórios
          </TabsTrigger>
          <TabsTrigger value="appearance">Aparência</TabsTrigger>
          
          {/* Nova Aba GPS - Renderização Condicional */}
          {hasGps && (
            <TabsTrigger value="gps" className="flex items-center gap-2">
              <Radio className="h-4 w-4 text-green-600" />
              GPS / Rastreamento
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="general" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <SettingsIcon className="h-5 w-5" />
                Configurações Gerais
              </CardTitle>
              <CardDescription>
                Configure as preferências básicas do sistema
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="timezone">Fuso Horário</Label>
                  <Select value={settings.general.timezone} onValueChange={(value) => updateGeneralSetting("timezone", value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="America/Sao_Paulo">América/São Paulo</SelectItem>
                      <SelectItem value="America/New_York">América/Nova York</SelectItem>
                      <SelectItem value="Europe/London">Europa/Londres</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="date_format">Formato de Data</Label>
                  <Select value={settings.general.date_format} onValueChange={(value) => updateGeneralSetting("date_format", value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DD/MM/YYYY">DD/MM/AAAA</SelectItem>
                      <SelectItem value="MM/DD/YYYY">MM/DD/AAAA</SelectItem>
                      <SelectItem value="YYYY-MM-DD">AAAA-MM-DD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="currency">Moeda</Label>
                  <Select value={settings.general.currency} onValueChange={(value) => updateGeneralSetting("currency", value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="BRL">Real (R$)</SelectItem>
                      <SelectItem value="USD">Dólar ($)</SelectItem>
                      <SelectItem value="EUR">Euro (€)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fuel_unit">Unidade de Combustível</Label>
                  <Select value={settings.general.fuel_unit} onValueChange={(value) => updateGeneralSetting("fuel_unit", value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="L">Litros</SelectItem>
                      <SelectItem value="GAL">Galões</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="working_hours_start">Início do Expediente</Label>
                  <Input
                    type="time"
                    value={settings.general.working_hours_start}
                    onChange={(e) => updateGeneralSetting("working_hours_start", e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="working_hours_end">Fim do Expediente</Label>
                  <Input
                    type="time"
                    value={settings.general.working_hours_end}
                    onChange={(e) => updateGeneralSetting("working_hours_end", e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notifications" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Configurações de Notificações
              </CardTitle>
              <CardDescription>
                Configure quando e como receber notificações
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <h4 className="text-sm font-medium">Tipos de Alertas</h4>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="maintenance_alerts" className="text-sm">
                      Alertas de Manutenção
                    </Label>
                    <Switch
                      id="maintenance_alerts"
                      checked={settings.notifications.maintenance_alerts}
                      onCheckedChange={(checked) => updateNotificationSetting("maintenance_alerts", checked)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="fuel_alerts" className="text-sm">
                      Alertas de Combustível
                    </Label>
                    <Switch
                      id="fuel_alerts"
                      checked={settings.notifications.fuel_alerts}
                      onCheckedChange={(checked) => updateNotificationSetting("fuel_alerts", checked)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="document_expiry" className="text-sm">
                      Vencimento de Documentos
                    </Label>
                    <Switch
                      id="document_expiry"
                      checked={settings.notifications.document_expiry}
                      onCheckedChange={(checked) => updateNotificationSetting("document_expiry", checked)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="violation_alerts" className="text-sm">
                      Alertas de Infrações
                    </Label>
                    <Switch
                      id="violation_alerts"
                      checked={settings.notifications.violation_alerts}
                      onCheckedChange={(checked) => updateNotificationSetting("violation_alerts", checked)}
                    />
                  </div>
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <h4 className="text-sm font-medium">Canais de Notificação</h4>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="email_notifications" className="text-sm">
                      Notificações por Email
                    </Label>
                    <Switch
                      id="email_notifications"
                      checked={settings.notifications.email_notifications}
                      onCheckedChange={(checked) => updateNotificationSetting("email_notifications", checked)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="sms_notifications" className="text-sm">
                      Notificações por SMS
                    </Label>
                    <Switch
                      id="sms_notifications"
                      checked={settings.notifications.sms_notifications}
                      onCheckedChange={(checked) => updateNotificationSetting("sms_notifications", checked)}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="telemetry" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                Configurações de Telemetria
              </CardTitle>
              <CardDescription>
                Configure a integração com provedores de telemetria
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="telemetry_provider">Provedor de Telemetria</Label>
                <Select value={settings.telemetry.provider} onValueChange={(value) => updateTelemetrySetting("provider", value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhum</SelectItem>
                    <SelectItem value="omnilink">Omnilink</SelectItem>
                    <SelectItem value="sascar">Sascar</SelectItem>
                    <SelectItem value="trackgps">TrackGPS</SelectItem>
                    <SelectItem value="custom">Personalizado</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {settings.telemetry.provider !== "none" && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="api_key">Chave da API</Label>
                    <Input
                      type="password"
                      placeholder="Digite a chave da API"
                      value={settings.telemetry.api_key || ""}
                      onChange={(e) => updateTelemetrySetting("api_key", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="sync_interval">Intervalo de Sincronização (segundos)</Label>
                    <Input
                      type="number"
                      value={settings.telemetry.sync_interval}
                      onChange={(e) => updateTelemetrySetting("sync_interval", parseInt(e.target.value))}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="real_time_tracking" className="text-sm">
                      Rastreamento em Tempo Real
                    </Label>
                    <Switch
                      id="real_time_tracking"
                      checked={settings.telemetry.real_time_tracking}
                      onCheckedChange={(checked) => updateTelemetrySetting("real_time_tracking", checked)}
                    />
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="permissions" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Controle de Acesso
              </CardTitle>
              <CardDescription>
                Configure permissões e restrições de acesso
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="all_users_view_costs" className="text-sm font-medium">
                      Todos os usuários podem ver custos
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Permite que todos os usuários vejam informações financeiras
                    </p>
                  </div>
                  <Switch
                    id="all_users_view_costs"
                    checked={settings.permissions.all_users_view_costs}
                    onCheckedChange={(checked) => updatePermissionSetting("all_users_view_costs", checked)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="restrict_vehicle_assignment" className="text-sm font-medium">
                      Restringir alocação de veículos
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Apenas administradores podem alocar veículos
                    </p>
                  </div>
                  <Switch
                    id="restrict_vehicle_assignment"
                    checked={settings.permissions.restrict_vehicle_assignment}
                    onCheckedChange={(checked) => updatePermissionSetting("restrict_vehicle_assignment", checked)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="require_approval_maintenance" className="text-sm font-medium">
                      Exigir aprovação para manutenção
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Ordens de serviço precisam de aprovação
                    </p>
                  </div>
                  <Switch
                    id="require_approval_maintenance"
                    checked={settings.permissions.require_approval_maintenance}
                    onCheckedChange={(checked) => updatePermissionSetting("require_approval_maintenance", checked)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="cost-centers" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Centro de Custo
              </CardTitle>
              <CardDescription>
                Gerencie os centros de custo da sua empresa
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Summary Cards */}
              <div className="grid gap-4 md:grid-cols-3">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total</CardTitle>
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{costCenters?.length || 0}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Ativos</CardTitle>
                    <Building2 className="h-4 w-4 text-green-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{activeCostCenters.length}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Inativos</CardTitle>
                    <Building2 className="h-4 w-4 text-red-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{inactiveCostCenters.length}</div>
                  </CardContent>
                </Card>
              </div>

              {/* Action Button */}
              <div className="flex justify-end">
                <Button
                  onClick={() => {
                    setSelectedCostCenter(null);
                    setCostCenterModalOpen(true);
                  }}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Novo Centro de Custo
                </Button>
              </div>

              {/* Cost Centers Table */}
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>Código</TableHead>
                      <TableHead>Descrição</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {costCenters?.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                          <Building2 className="h-12 w-12 mx-auto mb-4" />
                          <p>Nenhum centro de custo cadastrado</p>
                          <p className="text-sm">Clique em "Novo Centro de Custo" para começar</p>
                        </TableCell>
                      </TableRow>
                    ) : (
                      costCenters?.map((costCenter) => (
                        <TableRow key={costCenter.id}>
                          <TableCell className="font-medium">{costCenter.name}</TableCell>
                          <TableCell>{costCenter.code || "-"}</TableCell>
                          <TableCell>{costCenter.description || "-"}</TableCell>
                          <TableCell>
                            <Badge variant={costCenter.status === "active" ? "default" : "secondary"}>
                              {costCenter.status === "active" ? "Ativo" : "Inativo"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleEditCostCenter(costCenter)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeleteCostCenter(costCenter)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="provider-types" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Tag className="h-5 w-5" />
                Tipos de Fornecedor
              </CardTitle>
              <CardDescription>
                Configure os tipos de fornecedor disponíveis para sua empresa
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Summary Cards */}
              <div className="grid gap-4 md:grid-cols-3">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total</CardTitle>
                    <Tag className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{providerTypes?.length || 0}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Ativos</CardTitle>
                    <Tag className="h-4 w-4 text-green-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {providerTypes?.filter(t => t.is_active).length || 0}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Inativos</CardTitle>
                    <Tag className="h-4 w-4 text-red-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {providerTypes?.filter(t => !t.is_active).length || 0}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Action Button */}
              <div className="flex justify-end">
                <Button
                  onClick={() => {
                    setSelectedProviderType(null);
                    setProviderTypeModalOpen(true);
                  }}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Novo Tipo de Fornecedor
                </Button>
              </div>

              {/* Provider Types Table */}
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>Descrição</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {!providerTypes || providerTypes.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                          <Tag className="h-12 w-12 mx-auto mb-4" />
                          <p>Nenhum tipo de fornecedor cadastrado</p>
                          <p className="text-sm">Clique em "Novo Tipo de Fornecedor" para começar</p>
                        </TableCell>
                      </TableRow>
                    ) : (
                      providerTypes.map((type) => (
                        <TableRow key={type.id}>
                          <TableCell className="font-medium">{type.name}</TableCell>
                          <TableCell>{type.description || "-"}</TableCell>
                          <TableCell>
                            <Badge variant={type.is_active ? "default" : "secondary"}>
                              {type.is_active ? "Ativo" : "Inativo"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleEditProviderType(type)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeleteProviderType(type)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Aba de Configurações de Relatórios */}
        <TabsContent value="reports" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Configurações de Relatórios
              </CardTitle>
              <CardDescription>
                Personalize templates e configurações de relatórios da frota
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="text-center py-8">
                <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-medium mb-2">Editor de Templates</h3>
                <p className="text-muted-foreground mb-4 max-w-md mx-auto">
                  Personalize os relatórios da sua empresa: logo, cabeçalho, seções habilitadas, 
                  colunas visíveis e mais.
                </p>
                <Button 
                  onClick={() => window.location.href = '/fleet/settings/reports'}
                  className="gap-2"
                >
                  <SettingsIcon className="h-4 w-4" />
                  Abrir Editor de Relatórios
                </Button>
              </div>

              <Separator />

              <div className="grid gap-4 md:grid-cols-2">
                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium mb-2">Relatório Consolidado</h4>
                  <p className="text-sm text-muted-foreground">
                    Visão geral da frota com KPIs, custos por veículo e motorista
                  </p>
                </div>
                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium mb-2">Análise de Renovação</h4>
                  <p className="text-sm text-muted-foreground">
                    Recomendações de substituição e projeções de custos
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="appearance" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="h-5 w-5" />
                Aparência
              </CardTitle>
              <CardDescription>
                Personalize a aparência do módulo FleetONE
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8 text-muted-foreground">
                <Palette className="h-12 w-12 mx-auto mb-4" />
                <p>Configurações de aparência e personalização em breve</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Aba GPS / Rastreamento (Condicional) */}
        {hasGps && currentCompanyId && (
          <TabsContent value="gps" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Radio className="h-5 w-5 text-green-600" />
                  Configurações de GPS / Rastreamento
                </CardTitle>
                <CardDescription>
                  Configure o sistema de rastreamento GPS integrado
                </CardDescription>
              </CardHeader>
              <CardContent>
                <GpsSettingsContent companyId={currentCompanyId} />
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Cost Center Modal */}
      <CostCenterModal
        open={costCenterModalOpen}
        onOpenChange={(open) => {
          setCostCenterModalOpen(open);
          if (!open) setSelectedCostCenter(null);
        }}
        costCenter={selectedCostCenter}
        companyId={currentCompanyId || ""}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja remover o centro de custo "{costCenterToDelete?.name}"?
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteCostCenter}>
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Provider Type Modal */}
      <ProviderTypeModal
        open={providerTypeModalOpen}
        onOpenChange={(open) => {
          setProviderTypeModalOpen(open);
          if (!open) setSelectedProviderType(null);
        }}
        providerType={selectedProviderType}
        companyId={currentCompanyId || ""}
      />

      {/* Delete Provider Type Confirmation Dialog */}
      <AlertDialog open={deleteProviderTypeDialogOpen} onOpenChange={setDeleteProviderTypeDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja remover o tipo "{providerTypeToDelete?.name}"?
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteProviderType}>
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}